import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { 
  Mail, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Search, 
  Filter,
  Save,
  X,
  FileText,
  CheckCircle,
  XCircle
} from 'lucide-react';

const EmailTemplateManagement = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    isActive: '',
    search: ''
  });
  const [form, setForm] = useState({
    name: '',
    type: '',
    subject: '',
    htmlTemplate: '',
    textTemplate: '',
    description: '',
    category: '',
    isActive: true
  });

  useEffect(() => {
    fetchTemplates();
  }, [filters]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) queryParams.append(key, filters[key]);
      });

      const response = await fetch(
        `${API_BASE_URL}/api/admin/email-templates?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const url = editingTemplate
        ? `${API_BASE_URL}/api/admin/email-templates/${editingTemplate.id}`
        : `${API_BASE_URL}/api/admin/email-templates`;
      
      const method = editingTemplate ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        alert(editingTemplate ? 'Template updated successfully!' : 'Template created successfully!');
        setShowModal(false);
        setEditingTemplate(null);
        setForm({
          name: '',
          type: '',
          subject: '',
          htmlTemplate: '',
          textTemplate: '',
          description: '',
          category: '',
          isActive: true
        });
        fetchTemplates();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error || 'Failed to save template'}`);
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${API_BASE_URL}/api/admin/email-templates/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        alert('Template deleted successfully!');
        fetchTemplates();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error || 'Failed to delete template'}`);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    }
  };

  const handlePreview = async (template) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${API_BASE_URL}/api/admin/email-templates/${template.id}/preview`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            variables: {
              customerName: 'John Doe',
              orderNumber: 'DPC-123456',
              totalAmount: '2,500',
              deliveryDate: '2025-12-05'
            }
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPreviewData({ ...data, template });
      }
    } catch (error) {
      console.error('Error previewing template:', error);
    }
  };

  const emailTypes = [
    'ORDER_CONFIRMATION',
    'ORDER_RECEIPT',
    'ORDER_PROCESSING',
    'ORDER_SHIPPED',
    'ORDER_DELIVERED',
    'ORDER_CANCELLED',
    'PAYMENT_RECEIVED',
    'PAYMENT_FAILED',
    'DELIVERY_UPDATE',
    'MARKETING',
    'CUSTOM'
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Email Template Management</h2>
        <button
          onClick={() => {
            setEditingTemplate(null);
            setForm({
              name: '',
              type: '',
              subject: '',
              htmlTemplate: '',
              textTemplate: '',
              description: '',
              category: '',
              isActive: true
            });
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700]"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search templates..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All Categories</option>
              <option value="Order">Order</option>
              <option value="Payment">Payment</option>
              <option value="Delivery">Delivery</option>
              <option value="Marketing">Marketing</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.isActive}
              onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Templates List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-6 text-center text-gray-500">Loading templates...</div>
        ) : templates.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No templates found</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {templates.map((template) => (
              <div key={template.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                      {template.isActive ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-gray-400" />
                      )}
                      {template.isDefault && (
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                    <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                      <span>Type: {template.type}</span>
                      <span>Category: {template.category || 'N/A'}</span>
                      <span>Usage: {template._count?.emailLogs || 0}</span>
                      <span>Version: {template.version}</span>
                    </div>
                    <div className="mt-2">
                      <p className="text-sm font-medium text-gray-700">Subject:</p>
                      <p className="text-sm text-gray-600">{template.subject}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handlePreview(template)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Preview"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        setEditingTemplate(template);
                        setForm({
                          name: template.name,
                          type: template.type,
                          subject: template.subject,
                          htmlTemplate: template.htmlTemplate,
                          textTemplate: template.textTemplate || '',
                          description: template.description || '',
                          category: template.category || '',
                          isActive: template.isActive
                        });
                        setShowModal(true);
                      }}
                      className="p-2 text-[#d44100] hover:bg-orange-50 rounded-lg"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">
                {editingTemplate ? 'Edit Template' : 'Create Template'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingTemplate(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., ORDER_PROCESSING"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type *
                  </label>
                  <select
                    required
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Select Type</option>
                    {emailTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                  Subject *
                </label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Quick Insert:</span>
                    <button
                      type="button"
                      onClick={() => {
                        const input = document.querySelector('input[name="subject"]');
                        if (input) {
                          const start = input.selectionStart;
                          const end = input.selectionEnd;
                          const text = form.subject;
                          const before = text.substring(0, start);
                          const after = text.substring(end);
                          const newText = before + '{{orderNumber}}' + after;
                          setForm({ ...form, subject: newText });
                        }
                      }}
                      className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100"
                    >
                      + Order #
                    </button>
                  </div>
                </div>
                <input
                  name="subject"
                  type="text"
                  required
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Email subject line (use {{variables}})"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                  HTML Template *
                </label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Insert Field:</span>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          const textarea = document.querySelector('textarea[name="htmlTemplate"]');
                          if (textarea) {
                            const start = textarea.selectionStart;
                            const end = textarea.selectionEnd;
                            const text = form.htmlTemplate;
                            const before = text.substring(0, start);
                            const after = text.substring(end);
                            const newText = before + `{{${e.target.value}}}` + after;
                            setForm({ ...form, htmlTemplate: newText });
                            setTimeout(() => {
                              textarea.focus();
                              textarea.setSelectionRange(start + e.target.value.length + 4, start + e.target.value.length + 4);
                            }, 0);
                          }
                        }
                        e.target.value = '';
                      }}
                      className="text-xs px-2 py-1 border border-gray-300 rounded bg-white"
                    >
                      <option value="">Select field...</option>
                      <option value="customerName">Customer Name</option>
                      <option value="orderNumber">Order Number</option>
                      <option value="orderDate">Order Date</option>
                      <option value="totalAmount">Total Amount</option>
                      <option value="subtotal">Subtotal</option>
                      <option value="shipping">Shipping</option>
                      <option value="tax">Tax</option>
                      <option value="paymentMethod">Payment Method</option>
                      <option value="paymentStatus">Payment Status</option>
                      <option value="paymentDate">Payment Date</option>
                      <option value="paymentReference">Payment Reference</option>
                      <option value="receiptNumber">Receipt Number</option>
                      <option value="orderItems">Order Items (HTML)</option>
                      <option value="orderItemsText">Order Items (Text)</option>
                      <option value="fullShippingAddress">Full Shipping Address</option>
                      <option value="shippingAddress">Shipping Address</option>
                      <option value="shippingCity">Shipping City</option>
                      <option value="shippingCounty">Shipping County</option>
                      <option value="shippingCountry">Shipping Country</option>
                      <option value="storeEmail">Store Email</option>
                      <option value="storePhone">Store Phone</option>
                      <option value="currentYear">Current Year</option>
                    </select>
                  </div>
                </div>
                <textarea
                  name="htmlTemplate"
                  required
                  rows={12}
                  value={form.htmlTemplate}
                  onChange={(e) => setForm({ ...form, htmlTemplate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                  placeholder="HTML email template with {{variables}}"
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const textarea = document.querySelector('textarea[name="htmlTemplate"]');
                      if (textarea) {
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const text = form.htmlTemplate;
                        const before = text.substring(0, start);
                        const after = text.substring(end);
                        const newText = before + '{{customerName}}' + after;
                        setForm({ ...form, htmlTemplate: newText });
                      }
                    }}
                    className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                  >
                    + Customer Name
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const textarea = document.querySelector('textarea[name="htmlTemplate"]');
                      if (textarea) {
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const text = form.htmlTemplate;
                        const before = text.substring(0, start);
                        const after = text.substring(end);
                        const newText = before + '{{orderNumber}}' + after;
                        setForm({ ...form, htmlTemplate: newText });
                      }
                    }}
                    className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                  >
                    + Order Number
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const textarea = document.querySelector('textarea[name="htmlTemplate"]');
                      if (textarea) {
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const text = form.htmlTemplate;
                        const before = text.substring(0, start);
                        const after = text.substring(end);
                        const newText = before + '{{totalAmount}}' + after;
                        setForm({ ...form, htmlTemplate: newText });
                      }
                    }}
                    className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                  >
                    + Total Amount
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const textarea = document.querySelector('textarea[name="htmlTemplate"]');
                      if (textarea) {
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const text = form.htmlTemplate;
                        const before = text.substring(0, start);
                        const after = text.substring(end);
                        const newText = before + '{{orderItems}}' + after;
                        setForm({ ...form, htmlTemplate: newText });
                      }
                    }}
                    className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                  >
                    + Order Items
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Tip: Click the field buttons above or use the dropdown to insert variables at your cursor position
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Text Template (Optional)
                </label>
                <textarea
                  rows={5}
                  value={form.textTemplate}
                  onChange={(e) => setForm({ ...form, textTemplate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                  placeholder="Plain text version"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Template description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <input
                    type="text"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., Order, Payment"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  className="h-4 w-4 text-[#d44100] focus:ring-[#d44100] border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                  Active
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingTemplate(null);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700]"
                >
                  {editingTemplate ? 'Update' : 'Create'} Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Template Preview</h3>
              <button
                onClick={() => setPreviewData(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700">Subject:</label>
              <p className="text-sm text-gray-900">{previewData.subject}</p>
            </div>

            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700">HTML Preview:</label>
              <div
                className="mt-2 p-4 border border-gray-300 rounded-lg bg-gray-50"
                dangerouslySetInnerHTML={{ __html: previewData.html }}
              />
            </div>

            {previewData.text && (
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700">Text Version:</label>
                <pre className="mt-2 p-4 border border-gray-300 rounded-lg bg-gray-50 text-sm whitespace-pre-wrap">
                  {previewData.text}
                </pre>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={() => setPreviewData(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplateManagement;

